/**
 * S49.2 — the pre-expiry warning.
 *
 * ⚠️ THE "STAY SIGNED IN" BUTTON ONLY EXISTS BECAUSE RENEWAL DOES (S49.5/S49.6). When this
 * component first shipped it deliberately had no such button: there was no /auth/refresh
 * endpoint, so the control could not have worked, and a control that silently fails is worse
 * than none -- the user relies on it, keeps working, and loses the work anyway.
 *
 * ⚠️ MOST USERS SHOULD NEVER SEE THIS MODAL. `useSessionExpiry` attempts a silent renewal the
 * moment the warning window opens, so this appears only when that renewal FAILED -- an expired
 * refresh token, a revoked session, or a network that is down. The button is therefore a
 * retry, not the primary mechanism, and its failure path has to be honest rather than
 * optimistic.
 */

import { useState } from 'react';

import { Modal } from './ui/Modal';

function formatRemaining(seconds: number): string {
  if (seconds >= 60) {
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }
  return `${seconds} second${seconds === 1 ? '' : 's'}`;
}

export function SessionExpiryWarning({
  open,
  secondsRemaining,
  onDismiss,
  onSignInNow,
  onExtend,
}: {
  open: boolean;
  secondsRemaining: number | null;
  onDismiss: () => void;
  onSignInNow: () => void;
  onExtend: () => Promise<boolean>;
}) {
  const [extending, setExtending] = useState(false);
  const [extendFailed, setExtendFailed] = useState(false);

  if (!open || secondsRemaining === null) return null;

  const handleExtend = async () => {
    setExtending(true);
    setExtendFailed(false);
    const ok = await onExtend();
    setExtending(false);
    // ⚠️ Say so when it fails. Closing the modal on a failed renewal would tell the user they
    // are safe when they are about to be signed out -- the exact deception this whole sprint
    // is about.
    if (!ok) setExtendFailed(true);
  };

  return (
    <Modal
      open={open}
      title="Your session is about to expire"
      size="small"
      onClose={onDismiss}
      // ⚠️ Backdrop-close is disabled on purpose. A stray click dismissing the one warning a
      // user gets before losing unsaved work defeats the entire point of showing it.
      allowBackdropClose={false}
      footer={
        <>
          <button type="button" className="fbtn secondary" onClick={onSignInNow}>
            Sign in again
          </button>
          <button
            type="button"
            className={`fbtn primary${extending ? ' fbtn-loading' : ''}`}
            onClick={handleExtend}
            disabled={extending}
          >
            {extending ? 'Extending…' : 'Stay signed in'}
          </button>
        </>
      }
    >
      <div className="alert-box warning">
        <div className="alert-box-content">
          <div className="alert-box-title">
            Signing out in {formatRemaining(secondsRemaining)}
          </div>
          <div className="alert-box-message">
            {extendFailed
              ? 'Your session could not be extended — it may have already expired elsewhere. ' +
                'Save anything in progress and sign in again to continue.'
              : 'Save anything in progress. Choose “Stay signed in” to continue where you are.'}
          </div>
        </div>
      </div>
    </Modal>
  );
}
